import { useEffect, useState, useMemo, useRef } from "react";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import api, { getUser, isAuthed, errMsg } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MapPin, Star, Phone, Mail, CheckCircle2, ShieldCheck, Instagram, CreditCard, Building2, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { absUrl } from "@/components/PhotoUploader";
import ChatWidget from "@/components/ChatWidget";
import { useCurrency, CURRENCIES } from "@/lib/currency";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js";

// Cached Stripe.js promise (one per publishable key)
const _stripeCache = new Map();
function getStripe(pk) {
  if (!pk) return null;
  if (!_stripeCache.has(pk)) _stripeCache.set(pk, loadStripe(pk));
  return _stripeCache.get(pk);
}

function nightsBetween(a, b) {
  const d1 = new Date(a), d2 = new Date(b);
  return Math.max(1, Math.round((d2 - d1) / (86400*1000)));
}

function BookDialog({ property, room, initialCheckIn = "", initialCheckOut = "", initialPromoCode = "", payAtPropertyEnabled = false, publishableKey = "" }) {
  const { t, lang } = useLang();
  const { format, currency } = useCurrency();
  const IT = lang === "it";
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  // Pre-fill name/email if the user is logged in as a guest so they don't retype
  const authedGuest = isAuthed() && getUser()?.role === "guest" ? getUser() : null;
  const [name, setName] = useState(authedGuest?.name || "");
  const [email, setEmail] = useState(authedGuest?.email || "");
  const [phone, setPhone] = useState("");
  const [arrivalTime, setArrivalTime] = useState("");
  const [checkIn, setCheckIn] = useState(initialCheckIn); const [checkOut, setCheckOut] = useState(initialCheckOut);
  const [promoCode, setPromoCode] = useState(initialPromoCode); const [promoInfo, setPromoInfo] = useState(null);
  const [payMethod, setPayMethod] = useState("prepaid");  // prepaid | at_property
  const [loading, setLoading] = useState(false);
  const nights = checkIn && checkOut ? nightsBetween(checkIn, checkOut) : 0;
  const subtotal = nights * room.price_per_night;
  const discount = promoInfo ? subtotal * (promoInfo.percent_off / 100) : 0;
  const total = subtotal - discount;

  // Fetch the traveller's active LAUNCH5 promo the first time an authed
  // guest sees the dialog, so the 5% is applied without the user having to
  // type any code. Silent on 404 (no promo issued yet).
  useEffect(() => {
    if (!authedGuest || promoInfo) return;
    let cancelled = false;
    api.get("/guest/promos/launch")
      .then((r) => {
        if (cancelled || !r.data) return;
        setPromoCode(r.data.code);
        setPromoInfo({ code: r.data.code, percent_off: r.data.percent_off, type: "promo" });
      })
      .catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authedGuest?.email]);

  // Guests reach the dialog only after they've registered — clicking the
  // trigger while anonymous redirects them to the branded /guest/auth
  // flow which returns them here with the LAUNCH5 promo pre-applied.
  const requireAuth = () => {
    if (authedGuest) return true;
    const back = new URL(window.location.href);
    back.searchParams.set("room", room.id);
    if (checkIn) back.searchParams.set("checkIn", checkIn);
    if (checkOut) back.searchParams.set("checkOut", checkOut);
    const returnUrl = back.pathname + back.search;
    nav(`/guest/auth?mode=register&launch=1&return=${encodeURIComponent(returnUrl)}`);
    return false;
  };

  const applyPromo = async () => {
    if (!promoCode.trim() || !email) { toast.error(IT ? "Inserisci email e codice" : "Enter email and code"); return; }
    try {
      const r = await api.post("/promo/validate", { code: promoCode, guest_email: email });
      setPromoInfo(r.data);
      toast.success(t("vetrina.promo_valid") || "Valid");
    } catch (e) {
      setPromoInfo(null);
      toast.error(e?.response?.data?.detail || (IT ? "Codice non valido" : "Invalid code"));
    }
  };

  const submitPrepaid = async () => {
    if (!phone || phone.trim().length < 6) {
      toast.error(IT ? "Inserisci un numero di telefono valido" : "Please enter a valid phone number");
      return;
    }
    if (property?.require_arrival_time && !(arrivalTime || "").trim()) {
      toast.error(IT ? "Inserisci l'orario di arrivo previsto" : "Please enter your expected arrival time");
      return;
    }
    setLoading(true);
    try {
      const r = await api.post("/bookings/checkout", {
        property_slug: property.slug, room_id: room.id,
        guest_name: name, guest_email: email, guest_phone: phone.trim(),
        check_in: checkIn, check_out: checkOut, nights,
        promo_code: promoInfo ? promoCode : undefined,
        origin_url: window.location.origin,
        payment_method: "prepaid",
        arrival_time: (arrivalTime || "").trim() || undefined,
      });
      window.location.href = r.data.checkout_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
      setLoading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (payMethod === "prepaid") return submitPrepaid();
    // at_property flow is handled inside <AtPropertyForm> below
  };

  const commonFields = (
    <>
      {!authedGuest && (
        <div className="text-[11px] text-muted-foreground bg-sand/30 border border-border/60 rounded-md px-3 py-2 flex items-center justify-between gap-2" data-testid="booking-login-nudge">
          <span>{IT ? "Sei un viaggiatore?" : "You're a traveller?"}</span>
          <Link to="/guest/auth?mode=login" className="text-primary hover:underline whitespace-nowrap">
            {IT ? "Accedi per precompilare i tuoi dati →" : "Log in to prefill your details →"}
          </Link>
        </div>
      )}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>{t("vetrina.check_in")}</Label>
          <Input type="date" required value={checkIn} onChange={(e)=>setCheckIn(e.target.value)} data-testid="booking-checkin"/>
        </div>
        <div>
          <Label>{t("vetrina.check_out")}</Label>
          <Input type="date" required value={checkOut} onChange={(e)=>setCheckOut(e.target.value)} data-testid="booking-checkout"/>
        </div>
      </div>
      <div>
        <Label>{t("vetrina.guest_name")}</Label>
        <Input required value={name} onChange={(e)=>setName(e.target.value)} data-testid="booking-name"/>
      </div>
      <div>
        <Label>{t("vetrina.guest_email")}</Label>
        <Input type="email" required value={email} onChange={(e)=>setEmail(e.target.value)} data-testid="booking-email"/>
      </div>
      <div>
        <Label>{IT ? "Telefono" : "Phone"} <span className="text-destructive text-[10px]">*</span></Label>
        <Input
          type="tel" required minLength={6}
          value={phone}
          onChange={(e)=>setPhone(e.target.value)}
          placeholder={IT ? "+39 333 123 4567" : "+1 555 123 4567"}
          data-testid="booking-phone"
        />
        <div className="text-[10px] text-muted-foreground mt-1">
          {IT ? "L'host lo userà solo per contatti relativi a questa prenotazione." : "The host will only use it for booking-related contact."}
        </div>
      </div>
      <div>
        <Label>
          {IT ? "Orario di arrivo previsto" : "Expected arrival time"}
          {property?.require_arrival_time
            ? <span className="text-destructive text-[10px]"> *</span>
            : <span className="text-muted-foreground text-[10px]"> ({IT ? "opzionale" : "optional"})</span>}
        </Label>
        <Input
          type="time"
          value={arrivalTime}
          onChange={(e)=>setArrivalTime(e.target.value)}
          required={!!property?.require_arrival_time}
          data-testid="booking-arrival-time"
        />
        <div className="text-[10px] text-muted-foreground mt-1">
          {IT ? "Aiuta l'host a prepararsi per il tuo check-in." : "Helps the host get ready for your check-in."}
        </div>
      </div>
    </>
  );

  const priceBox = nights > 0 && (
    <>
      <div className="flex gap-2">
        <Input placeholder={t("vetrina.promo_code") || "Promo"} value={promoCode} onChange={(e)=>{setPromoCode(e.target.value); setPromoInfo(null);}} data-testid="booking-promo"/>
        <Button type="button" variant="outline" onClick={applyPromo} className="lift shrink-0" data-testid="booking-promo-apply">{t("vetrina.promo_apply") || "Apply"}</Button>
      </div>
      <div className="bg-sand p-3 rounded-md border border-border space-y-1">
        <div className="flex justify-between text-sm"><span>{nights} × {format(room.price_per_night)}</span><span>{format(subtotal)}</span></div>
        {promoInfo && (
          <div className="flex justify-between text-sm text-sage" data-testid="booking-discount">
            <span>{t("vetrina.promo_saved") || "You save"} ({promoInfo.percent_off}%)</span>
            <span>− {format(discount)}</span>
          </div>
        )}
        <div className="flex items-end justify-between pt-2 border-t border-border/60">
          <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{t("dash.total")}</span>
          <div className="text-right">
            <div className="font-display text-2xl text-primary" data-testid="booking-total">{format(total)}</div>
            {currency !== "EUR" && <div className="text-[10px] text-muted-foreground uppercase tracking-[0.2em]">{IT ? "Addebito in EUR" : "Charged in EUR"} · € {total.toFixed(2)}</div>}
          </div>
        </div>
      </div>
    </>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => { if (o && !requireAuth()) return; setOpen(o); }}>
      <DialogTrigger asChild>
        <Button className="bg-primary text-primary-foreground hover:bg-primary/90 lift" data-testid={`book-btn-${room.id}`}>
          {t("vetrina.book_this_room")}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-3xl">{room.name}</DialogTitle>
          <DialogDescription>{property.name} · {property.city}</DialogDescription>
        </DialogHeader>

        {/* Cancellation policy — room-level override falls back to property */}
        <CancellationPolicyBanner
          kind={room?.no_show_penalty || property?.no_show_penalty || "full"}
          checkIn={checkIn}
          nights={nights}
          total={total}
          pricePerNight={room?.price_per_night}
          IT={IT}
        />

        {/* Payment method selector */}
        {payAtPropertyEnabled && (
          <div className="grid grid-cols-2 gap-2" data-testid="payment-method-selector">
            <button type="button" onClick={()=>setPayMethod("prepaid")} data-testid="pay-prepaid-btn"
              className={`text-left border rounded-lg p-3 transition-all lift ${payMethod==="prepaid" ? "border-primary bg-primary/5" : "border-border bg-card"}`}>
              <div className="flex items-center gap-2 mb-1"><CreditCard className="w-4 h-4 text-primary"/><span className="font-semibold text-sm">{IT ? "Paga ora" : "Pay now"}</span></div>
              <div className="text-[11px] text-muted-foreground leading-snug">{IT ? "Addebito immediato via Stripe" : "Immediate charge via Stripe"}</div>
            </button>
            <button type="button" onClick={()=>setPayMethod("at_property")} data-testid="pay-at-property-btn"
              className={`text-left border rounded-lg p-3 transition-all lift ${payMethod==="at_property" ? "border-primary bg-primary/5" : "border-border bg-card"}`}>
              <div className="flex items-center gap-2 mb-1"><Building2 className="w-4 h-4 text-primary"/><span className="font-semibold text-sm">{IT ? "Paga in struttura" : "Pay at property"}</span></div>
              <div className="text-[11px] text-muted-foreground leading-snug">{IT ? "Carta a garanzia · addebito solo se no-show" : "Card guarantee · charged only on no-show"}</div>
            </button>
          </div>
        )}

        {payMethod === "prepaid" ? (
          <form onSubmit={submit} className="space-y-4" data-testid="booking-form">
            {commonFields}
            {priceBox}
            <Button type="submit" disabled={loading || !nights} className="w-full bg-primary text-primary-foreground hover:bg-primary/90 lift" data-testid="booking-submit">
              {loading ? t("vetrina.processing") : t("vetrina.confirm_book")}
            </Button>
          </form>
        ) : (
          <div className="space-y-4">
            {commonFields}
            <div>
              <Label>{IT ? "Telefono (opzionale)" : "Phone (optional)"}</Label>
              <Input value={phone} onChange={(e)=>setPhone(e.target.value)} placeholder="+39 ..." data-testid="booking-phone"/>
            </div>
            {priceBox}
            <Elements stripe={getStripe(publishableKey)}>
              <AtPropertyForm
                property={property} room={room}
                name={name} email={email} phone={phone}
                checkIn={checkIn} checkOut={checkOut} nights={nights}
                promoCode={promoInfo ? promoCode : ""}
                arrivalTime={arrivalTime}
                total={total}
                noShowPenaltyKind={property?.no_show_penalty || "full"}
                onDone={(bookingId)=>{ setOpen(false); }}
              />
            </Elements>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function CancellationPolicyBanner({ kind, checkIn, nights, total, pricePerNight, IT }) {
  const k = (kind || "full").toLowerCase();
  const freeDays = { free_5d: 5, free_3d: 3, free_1d: 1 }[k] || 0;
  const isFree = freeDays > 0;
  const deadline = (isFree && checkIn) ? new Date(new Date(checkIn).getTime() - freeDays * 86400000) : null;
  const penalty = (() => {
    if (!nights) return null;
    if (k === "first_night") return Math.round((Number(pricePerNight) || total / Math.max(nights, 1)) * 100) / 100;
    if (k === "30_percent") return Math.round(total * 0.30 * 100) / 100;
    return Math.round(total * 100) / 100;
  })();
  const badge = isFree
    ? (IT ? "Cancellazione gratuita" : "Free cancellation")
    : (IT ? "No-show policy" : "No-show policy");
  const badgeCls = isFree ? "bg-sage/15 text-sage" : "bg-amber-50 text-amber-800 border-amber-200";
  return (
    <div className={`border rounded-md p-3 mb-1 ${isFree ? "border-sage/30 bg-sage/5" : "border-amber-200 bg-amber-50/60"}`} data-testid="checkout-policy-banner">
      <div className="flex items-center justify-between gap-3">
        <div className="text-[10px] uppercase tracking-[0.2em] font-semibold px-2 py-0.5 rounded-full inline-block" style={{}}>
          <span className={badgeCls + " px-2 py-0.5 rounded-full"}>{badge}</span>
        </div>
        {penalty && !isFree && (
          <div className="text-xs text-amber-900"><span className="opacity-70">{IT ? "Penale" : "Penalty"}: </span><strong>€ {penalty.toFixed(2)}</strong></div>
        )}
      </div>
      <div className="mt-2 text-[12px] leading-relaxed text-foreground/80" data-testid="checkout-policy-text">
        {isFree ? (
          IT
            ? <>Puoi cancellare gratuitamente fino a <strong>{freeDays} {freeDays === 1 ? "giorno" : "giorni"} prima del check-in</strong>{deadline ? <> (entro il <strong>{deadline.toLocaleDateString("it-IT", {day:"numeric", month:"short", year:"numeric"})}</strong>)</> : null}. Dopo, l'importo viene trattenuto{penalty ? <> (€ {penalty.toFixed(2)})</> : null}.</>
            : <>Free cancellation up to <strong>{freeDays} day{freeDays === 1 ? "" : "s"} before check-in</strong>{deadline ? <> (by <strong>{deadline.toLocaleDateString("en-US", {day:"numeric", month:"short", year:"numeric"})}</strong>)</> : null}. After that, the amount is retained{penalty ? <> (€ {penalty.toFixed(2)})</> : null}.</>
        ) : (
          IT
            ? <>Cancellazione soggetta a penale: <strong>{k === "30_percent" ? "30% del totale" : k === "first_night" ? "prima notte" : "100% del totale"}</strong>{penalty ? <> — <strong>€ {penalty.toFixed(2)}</strong></> : null}.</>
            : <>Cancellation is subject to a penalty of <strong>{k === "30_percent" ? "30% of total" : k === "first_night" ? "first night" : "100% of total"}</strong>{penalty ? <> — <strong>€ {penalty.toFixed(2)}</strong></> : null}.</>
        )}
      </div>
    </div>
  );
}

function AtPropertyForm({ property, room, name, email, phone, checkIn, checkOut, nights, promoCode, arrivalTime = "", total, noShowPenaltyKind = "full", onDone }) {
  const { lang } = useLang();
  const IT = lang === "it";
  const stripe = useStripe();
  const elements = useElements();
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [cardReady, setCardReady] = useState(false);

  // Compute the guest-facing penalty preview from the property's chosen policy
  const freeCancelDays = { free_5d: 5, free_3d: 3, free_1d: 1 }[(noShowPenaltyKind || "").toLowerCase()] || 0;
  const isFreeCancellation = freeCancelDays > 0;
  const freeCancelDeadline = (isFreeCancellation && checkIn)
    ? new Date(new Date(checkIn).getTime() - freeCancelDays * 86400000)
    : null;
  const penaltyAmount = (() => {
    if (!nights) return 0;
    const kind = (noShowPenaltyKind || "full").toLowerCase();
    if (kind === "30_percent") return Math.round(total * 0.30 * 100) / 100;
    if (kind === "first_night") return Math.round(Number(room?.price_per_night || total/Math.max(nights,1)) * 100) / 100;
    // free_5d/3d/1d → after the window, 100% is due
    return Math.round(total * 100) / 100;
  })();
  const penaltyLabel = (() => {
    const kind = (noShowPenaltyKind || "full").toLowerCase();
    if (IT) return { full: "100% del totale", "30_percent": "30% del totale", first_night: "prima notte",
      free_5d: "cancellazione gratuita fino a 5 giorni prima", free_3d: "cancellazione gratuita fino a 3 giorni prima", free_1d: "cancellazione gratuita fino a 1 giorno prima" }[kind] || "100% del totale";
    return { full: "100% of total", "30_percent": "30% of total", first_night: "first night",
      free_5d: "free cancellation up to 5 days before", free_3d: "free cancellation up to 3 days before", free_1d: "free cancellation up to 1 day before" }[kind] || "100% of total";
  })();

  const submit = async (e) => {
    e.preventDefault();
    if (!name || !email) { toast.error(IT ? "Nome ed email obbligatori" : "Name and email required"); return; }
    if (!checkIn || !checkOut) { toast.error(IT ? "Seleziona le date" : "Pick your dates"); return; }
    if (!consent) { toast.error(IT ? "Devi accettare la politica no-show per continuare" : "You must accept the no-show policy to continue"); return; }
    if (property?.require_arrival_time && !(arrivalTime || "").trim()) { toast.error(IT ? "Inserisci l'orario di arrivo previsto" : "Please enter your expected arrival time"); return; }
    if (!stripe || !elements) return;
    if (!nights) { toast.error(IT ? "Seleziona le date" : "Pick your dates"); return; }
    setLoading(true);
    try {
      // 1) Create booking + SetupIntent server-side
      const create = await api.post("/bookings/checkout", {
        property_slug: property.slug, room_id: room.id,
        guest_name: name, guest_email: email,
        check_in: checkIn, check_out: checkOut, nights,
        promo_code: promoCode || undefined,
        origin_url: window.location.origin,
        payment_method: "at_property",
        arrival_time: (arrivalTime || "").trim() || undefined,
      });
      const { client_secret, booking_id, setup_intent_id } = create.data;
      // 2) Confirm SetupIntent with the card the guest just typed
      const card = elements.getElement(CardElement);
      const conf = await stripe.confirmCardSetup(client_secret, {
        payment_method: { card, billing_details: { name, email, phone: phone || undefined } },
      });
      if (conf.error) {
        toast.error(conf.error.message || "Card error");
        setLoading(false); return;
      }
      // 3) Backend confirms + places €1 hold + releases + marks guaranteed
      const done = await api.post("/bookings/at-property/confirm", {
        booking_id, setup_intent_id, consent_no_show: true, guest_phone: phone || "",
      });
      toast.success(IT
        ? `Prenotazione confermata · Carta •••• ${done.data.card_last4 || ""} salvata a garanzia`
        : `Booking confirmed · Card •••• ${done.data.card_last4 || ""} on file`
      );
      onDone?.(booking_id);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="at-property-form">
      <div className="border border-border rounded-md p-3 bg-card">
        <Label className="mb-2 block">{IT ? "Carta a garanzia" : "Guarantee card"}</Label>
        <div className="border border-border rounded-md p-3 bg-background" data-testid="stripe-card-element">
          <CardElement
            onChange={(e)=>setCardReady(e.complete)}
            options={{ hidePostalCode: true, style: { base: { fontSize: "15px", color: "#231F1B", fontFamily: "Manrope, Arial, sans-serif", "::placeholder": { color: "#9c9c9c" } } } }}
          />
        </div>
        <p className="text-[11px] text-muted-foreground mt-2 leading-relaxed">
          {IT
            ? "Non verrà effettuato alcun addebito ora. Faremo solo una verifica temporanea di 1€ (rilasciata subito) per validare la carta."
            : "No charge now. We will only run a temporary €1 verification (released immediately) to validate the card."}
        </p>
      </div>
      <label className="flex items-start gap-3 p-3 border border-border rounded-md bg-sand/50 cursor-pointer" data-testid="no-show-consent-row">
        <input type="checkbox" checked={consent} onChange={(e)=>setConsent(e.target.checked)} required data-testid="no-show-consent-checkbox" className="mt-1 accent-primary w-4 h-4"/>
        <div className="text-[12px] leading-relaxed">
          <div className="font-semibold flex items-center gap-1.5 mb-1"><ShieldAlert className="w-3.5 h-3.5 text-primary"/>{isFreeCancellation ? (IT ? "Cancellazione gratuita" : "Free cancellation") : (IT ? "Politica no-show" : "No-show policy")}</div>
          <span className="text-muted-foreground">
            {isFreeCancellation ? (
              IT
                ? <>Puoi cancellare gratuitamente fino a <strong className="text-foreground">{freeCancelDays} {freeCancelDays === 1 ? "giorno" : "giorni"} prima del check-in</strong>{freeCancelDeadline ? <> (entro il <strong className="text-foreground">{freeCancelDeadline.toLocaleDateString("it-IT", {day:"numeric", month:"short", year:"numeric"})}</strong>)</> : null}. Oltre questa scadenza, in caso di no-show, verrà addebitato l'intero importo{nights > 0 ? <> di <strong className="text-foreground">€ {penaltyAmount.toFixed(2)}</strong></> : null} sulla carta indicata.</>
                : <>You can cancel free of charge up to <strong className="text-foreground">{freeCancelDays} day{freeCancelDays === 1 ? "" : "s"} before check-in</strong>{freeCancelDeadline ? <> (by <strong className="text-foreground">{freeCancelDeadline.toLocaleDateString("en-US", {day:"numeric", month:"short", year:"numeric"})}</strong>)</> : null}. After that deadline, on no-show the full amount{nights > 0 ? <> of <strong className="text-foreground">€ {penaltyAmount.toFixed(2)}</strong></> : null} will be charged to the card above.</>
            ) : (IT
              ? <>Accetto che, in caso di mancata presentazione (no-show), venga addebitata una penale pari a <strong className="text-foreground">{penaltyLabel}</strong>{nights > 0 ? <> — <strong className="text-foreground">€ {penaltyAmount.toFixed(2)}</strong></> : null} sulla carta indicata.</>
              : <>I accept that if I fail to show up (no-show), a penalty of <strong className="text-foreground">{penaltyLabel}</strong>{nights > 0 ? <> — <strong className="text-foreground">€ {penaltyAmount.toFixed(2)}</strong></> : null} will be charged to the card above.</>)}
          </span>
        </div>
      </label>
      <Button type="submit" disabled={loading || !nights || !consent || !cardReady || !stripe || !name || !email} className="w-full bg-primary text-primary-foreground hover:bg-primary/90 lift" data-testid="at-property-submit">
        {loading ? (IT ? "Attendere…" : "Please wait…") : (IT ? "Conferma prenotazione (senza addebito)" : "Confirm booking (no charge)")}
      </Button>
    </form>
  );
}

function ReviewForm({ slug, onDone }) {
  const { t, lang } = useLang();
  const IT = lang === "it";
  const authedGuest = isAuthed() && getUser()?.role === "guest" ? getUser() : null;
  const [name, setName] = useState(authedGuest?.name || "");
  const [rating, setRating] = useState("5"); const [comment, setComment] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/reviews", { property_slug: slug, guest_name: name || (authedGuest?.name || "Ospite"), rating: parseInt(rating), comment });
      toast.success(IT ? "Grazie!" : "Thanks!");
      setComment(""); if (!authedGuest) setName("");
      onDone?.();
    } catch { toast.error("Error"); }
  };
  return (
    <form onSubmit={submit} className="space-y-3 border-t border-border pt-8" data-testid="review-form">
      <h3 className="font-display text-2xl">{t("vetrina.leave_review")}</h3>
      {authedGuest && (
        <div className="text-xs text-muted-foreground flex items-center gap-2">
          {authedGuest.picture && <img src={authedGuest.picture} alt="" className="w-6 h-6 rounded-full object-cover border border-border"/>}
          <span>{IT ? "Pubblicherai come" : "Posting as"} <strong className="text-foreground">{authedGuest.name || authedGuest.email}</strong></span>
        </div>
      )}
      <div className="grid sm:grid-cols-2 gap-3">
        {!authedGuest && (
          <Input placeholder={t("vetrina.guest_name")} value={name} onChange={(e)=>setName(e.target.value)} required data-testid="review-name"/>
        )}
        <Select value={rating} onValueChange={setRating}>
          <SelectTrigger data-testid="review-rating"><SelectValue/></SelectTrigger>
          <SelectContent>
            {[5,4,3,2,1].map((v)=><SelectItem key={v} value={String(v)}>{"★".repeat(v)}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <Textarea placeholder={t("vetrina.comment")} value={comment} onChange={(e)=>setComment(e.target.value)} required rows={3} data-testid="review-comment"/>
      <Button type="submit" variant="outline" className="lift" data-testid="review-submit">{t("vetrina.submit")}</Button>
    </form>
  );
}

export default function Vetrina() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const { lang, t } = useLang();
  const { format, currency, setCurrency, ready } = useCurrency();
  const nav = useNavigate();
  const location = useLocation();

  // Pre-fill booking dates from directory search (`?check_in=…&check_out=…`)
  const prefill = useMemo(() => {
    const s = new URLSearchParams(location.search);
    return { checkIn: s.get("check_in") || "", checkOut: s.get("check_out") || "" };
  }, [location.search]);

  const load = () => api.get(`/properties/${slug}`).then((r)=>setData(r.data)).catch(()=>nav("/"));
  useEffect(() => { load(); }, [slug]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!data) return <div className="min-h-screen grid place-items-center">{t("common.loading")}</div>;
  const { property: p, rooms, reviews } = data;
  const avg = reviews.length ? (reviews.reduce((s,r)=>s+r.rating,0)/reviews.length).toFixed(1) : null;
  const desc = lang === "it" ? p.description_it : p.description_en;

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      {/* Hero */}
      <section className="relative">
        <div className="grid lg:grid-cols-3 gap-2 p-2">
          <div className="lg:col-span-2 aspect-[4/3] lg:aspect-auto lg:h-[560px] rounded-lg overflow-hidden">
            <img src={absUrl(p.hero_image || p.photos?.[0])} alt={p.name} className="w-full h-full object-cover"/>
          </div>
          <div className="hidden lg:grid grid-rows-2 gap-2">
            {(p.photos || []).slice(1,3).map((ph) => (
              <div key={ph} className="rounded-lg overflow-hidden"><img src={absUrl(ph)} className="w-full h-full object-cover"/></div>
            ))}
          </div>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-6 py-16 grid lg:grid-cols-3 gap-12">
        <div className="lg:col-span-2">
          <div className="uppercase tracking-[0.25em] text-xs text-sage font-semibold mb-3 flex items-center gap-2" data-testid="vetrina-location">
            <MapPin className="w-3.5 h-3.5"/> {p.city}, {p.region}
          </div>
          <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
            <div className="flex items-start gap-3">
              <h1 className="font-display text-5xl sm:text-6xl tracking-tight leading-none" data-testid="vetrina-title">{p.name}</h1>
              {p.verified && (
                <span className="inline-flex items-center gap-1 text-xs bg-sage text-white rounded-full px-2.5 py-1 shrink-0 mt-2" data-testid="verified-badge" title="Struttura verificata">
                  <ShieldCheck className="w-3 h-3"/> {lang === "it" ? "Verificata" : "Verified"}
                </span>
              )}
            </div>
            <div className="inline-flex items-center border border-border rounded-full overflow-hidden bg-card" data-testid="currency-toggle">
              {CURRENCIES.map((c) => {
                const disabled = c !== "EUR" && !ready;
                return (
                  <button
                    key={c}
                    onClick={() => !disabled && setCurrency(c)}
                    disabled={disabled}
                    title={disabled ? (lang === "it" ? "Tassi in caricamento..." : "Loading rates...") : c}
                    data-testid={`ccy-${c}`}
                    className={`px-3 py-1.5 text-xs font-medium lift ${currency === c ? "bg-primary text-primary-foreground" : "hover:bg-accent"} ${disabled ? "opacity-40 cursor-not-allowed" : ""}`}
                  >{c}</button>
                );
              })}
            </div>
          </div>
          {avg && (
            <div className="flex items-center gap-2 mb-6 text-sm" data-testid="vetrina-rating">
              <Star className="w-4 h-4 fill-primary text-primary"/> <span className="font-semibold">{avg}</span>
              <span className="text-muted-foreground">· {reviews.length} {t("vetrina.reviews").toLowerCase()}</span>
            </div>
          )}
          <p className="text-lg text-muted-foreground leading-relaxed max-w-2xl">{desc}</p>

          <div className="mt-10 flex flex-wrap gap-2">
            {(p.amenities || []).map((a) => (
              <span key={a} className="inline-flex items-center gap-1.5 text-xs bg-sand border border-border rounded-full px-3 py-1.5">
                <CheckCircle2 className="w-3.5 h-3.5 text-sage"/> {a}
              </span>
            ))}
          </div>

          {/* Rooms */}
          <section id="rooms" className="mt-16">
            <h2 className="font-display text-4xl tracking-tight mb-8">{t("vetrina.rooms")}</h2>
            <div className="space-y-6" data-testid="vetrina-rooms">
              {rooms.map((r) => (
                <div key={r.id} className="grid sm:grid-cols-5 gap-6 border-t border-border pt-6" data-testid={`room-${r.id}`}>
                  <div className="sm:col-span-2 aspect-[4/3] rounded-lg overflow-hidden bg-muted">
                    {r.photo && <img src={absUrl(r.photo)} className="w-full h-full object-cover"/>}
                  </div>
                  <div className="sm:col-span-3 flex flex-col">
                    <h3 className="font-display text-2xl mb-1">{r.name}</h3>
                    <p className="text-sm text-muted-foreground flex-1">{lang==="it" ? r.description_it : r.description_en}</p>
                    <div className="mt-4 flex items-center justify-between flex-wrap gap-3">
                      <div>
                        <div className="font-display text-3xl text-primary">{format(r.price_per_night, { digits: 0 })}</div>
                        <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{t("vetrina.per_night")}</div>
                        {(() => {
                          const kind = (p.no_show_penalty || data.no_show_penalty || "").toLowerCase();
                          const days = { free_5d: 5, free_3d: 3, free_1d: 1 }[kind];
                          if (!days) return null;
                          return (
                            <span className="inline-flex items-center gap-1 mt-2 text-[10px] font-medium bg-sage/15 text-sage border border-sage/40 rounded-full px-2 py-1" data-testid={`free-cancel-badge-${r.id}`}>
                              <CheckCircle2 className="w-3 h-3"/>
                              {lang === "it" ? `Cancellazione gratuita fino a ${days} ${days === 1 ? "giorno" : "giorni"} prima` : `Free cancellation up to ${days} day${days === 1 ? "" : "s"} before`}
                            </span>
                          );
                        })()}
                      </div>
                      <BookDialog property={p} room={r} initialCheckIn={prefill.checkIn} initialCheckOut={prefill.checkOut} payAtPropertyEnabled={data.pay_at_property_enabled} publishableKey={data.stripe_publishable_key}/>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Instagram gallery */}
          {p.instagram_posts && p.instagram_posts.length > 0 && (
            <section id="instagram" className="mt-20">
              <div className="flex items-center gap-2 mb-6">
                <Instagram className="w-5 h-5 text-primary"/>
                <h2 className="font-display text-4xl tracking-tight">Instagram</h2>
                {p.instagram_handle && (
                  <a href={`https://instagram.com/${p.instagram_handle}`} target="_blank" rel="noopener noreferrer" className="text-sm text-sage hover:text-primary ml-2">@{p.instagram_handle}</a>
                )}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="ig-gallery">
                {p.instagram_posts.slice(0, 6).map((post, i) => (
                  <a key={post.image_url || i} href={post.link || `https://instagram.com/${p.instagram_handle}`} target="_blank" rel="noopener noreferrer"
                     className="relative group aspect-square overflow-hidden rounded-md" data-testid={`ig-post-${i}`}>
                    <img src={absUrl(post.image_url)} alt={post.caption || ""} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"/>
                    <div className="absolute inset-0 bg-ink/0 group-hover:bg-ink/40 transition-colors grid place-items-center opacity-0 group-hover:opacity-100">
                      <Instagram className="w-6 h-6 text-white"/>
                    </div>
                  </a>
                ))}
              </div>
            </section>
          )}

          {/* Reviews */}
          <section id="reviews" className="mt-20">
            <h2 className="font-display text-4xl tracking-tight mb-8">{t("vetrina.reviews")}</h2>            <div className="space-y-4 mb-8" data-testid="vetrina-reviews">
              {reviews.map((r) => (
                <div key={r.id} className="border border-border rounded-lg p-5 bg-card">
                  <div className="flex items-center justify-between mb-2 gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      {r.guest_picture ? (
                        <img src={r.guest_picture} alt={r.guest_name} className="w-9 h-9 rounded-full object-cover border border-border"/>
                      ) : (
                        <div className="w-9 h-9 rounded-full bg-sand grid place-items-center text-sm font-semibold text-primary">
                          {(r.guest_name || "?").charAt(0).toUpperCase()}
                        </div>
                      )}
                      <div className="min-w-0">
                        <div className="font-semibold truncate">{r.guest_name || (lang === "it" ? "Ospite" : "Guest")}</div>
                        {r.created_at && (
                          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                            {new Date(r.created_at).toLocaleDateString(lang === "it" ? "it-IT" : "en-US", { month: "short", year: "numeric" })}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="text-primary shrink-0">{"★".repeat(r.rating)}<span className="opacity-30">{"★".repeat(5-r.rating)}</span></div>
                  </div>
                  <p className="text-sm text-muted-foreground">{r.comment}</p>
                </div>
              ))}
            </div>
            <ReviewForm slug={slug} onDone={load}/>
          </section>
        </div>

        <aside className="lg:col-span-1">
          <div className="border border-border rounded-lg p-6 bg-card sticky top-24">
            <h3 className="font-display text-2xl mb-4">{t("vetrina.contact")}</h3>
            <div className="space-y-3 text-sm">
              <div className="flex items-start gap-3"><MapPin className="w-4 h-4 mt-0.5 text-sage"/> <span>{p.address}</span></div>
              {p.phone && <div className="flex items-center gap-3"><Phone className="w-4 h-4 text-sage"/> <a href={`tel:${p.phone}`} className="hover:text-primary">{p.phone}</a></div>}
              {p.email && <div className="flex items-center gap-3"><Mail className="w-4 h-4 text-sage"/> <a href={`mailto:${p.email}`} className="hover:text-primary">{p.email}</a></div>}
            </div>
          </div>
        </aside>
      </div>
      <ChatWidget propertySlug={slug} propertyName={p.name} propertyPhone={p.phone}/>
    </div>
  );
}
